import { ComponentType, createContext, FC, PropsWithChildren, PureComponent, ReactNode, useEffect, useState } from "react";
import MappingService from "./mappingService";
import { LngLatBoundsLike, PaddingOptions } from "mapbox-gl";
import * as ReactMapGL from "react-map-gl/mapbox";

export interface MappingContextProps {
  defaultBearing: number;
  defaultBoundaries: LngLatBoundsLike;
  defaultDragRotate: boolean;
  defaultLatitude: number;
  defaultLongitude: number;
  defaultMapProps: ReactMapGL.MapProps;
  defaultPadding: PaddingOptions
  defaultPitch: number;
  defaultTouchPitch: boolean;
  defaultViewState: ReactMapGL.ViewState;
  defaultZoom: number;
  errorLoadingSettings: boolean;
  loadingDefaultSettings: boolean;
  mapboxApiKey: string;
  qesLightStyle: string;
  qesOutdoorsStyle: string;
}

export const MappingContext = createContext<MappingContextProps>(null);

export const MappingContextProvider: FC<{ children: ReactNode }> = (props: { children: ReactNode }) => {

  const QES_LIGHT_STYLE = 'mapbox://styles/questanalytics/cmabl1g9k006801rz6hdp4onw';
  const QES_OUTDOORS_STYLE = 'mapbox://styles/questanalytics/cmabgskip004501s8etygafox';

  const [defaultBearing, setDefaultBearing] = useState<number>();
  const [defaultBoundaries, setDefaultBoundaries] = useState<LngLatBoundsLike>();
  const [defaultDragRotate, setDefaultDragRotate] = useState<boolean>();
  const [defaultLatitude, setDefaultLatitude] = useState<number>();
  const [defaultLongitude, setDefaultLongitude] = useState<number>();
  const [defaultPadding, setDefaultPadding] = useState<PaddingOptions>();
  const [defaultPitch, setDefaultPitch] = useState<number>();
  const [defaultTouchPitch, setDefaultTouchPitch] = useState<boolean>();
  const [defaultZoom, setDefaultZoom] = useState<number>();
  const [errorLoadingSettings, setErrorLoadingSettings] = useState<boolean>(false);
  const [loadingDefaultSettings, setLoadingDefaultSettings] = useState<boolean>(true);
  const [mapboxApiKey, setMapboxApiKey] = useState<string>('');
  const [qesLightStyle, setQESLightStyle] = useState<string>(QES_LIGHT_STYLE);
  const [qesOutdoorsStyle, setQESOutdoorsStyle] = useState<string>(QES_OUTDOORS_STYLE);

  useEffect(() => {
    const mappingService = new MappingService();
    mappingService.getDefaultSettings().then((result) => {
      setDefaultBearing(0);
      setDefaultBoundaries([
        result.southWestDefaultBoundaryLongitude,
        result.southWestDefaultBoundaryLatitude,
        result.northEastDefaultBoundaryLongitude,
        result.northEastDefaultBoundaryLatitude
      ]);
      setDefaultDragRotate(false);
      setDefaultLatitude(result.defaultLatitude);
      setDefaultLongitude(result.defaultLongitude);
      setDefaultPadding({ bottom: 0, left: 0, right: 0, top: 0 });
      setDefaultPitch(0);
      setDefaultTouchPitch(false);
      setDefaultZoom(4);
      setMapboxApiKey(result.mapBoxApiKey);
      setQESLightStyle(QES_LIGHT_STYLE);
      setQESOutdoorsStyle(QES_OUTDOORS_STYLE);

      setLoadingDefaultSettings(false);
    }).catch(() => {
      setLoadingDefaultSettings(false);
      setErrorLoadingSettings(true);
    });
  }, []);

  const defaultViewState: ReactMapGL.ViewState = {
    bearing: defaultBearing,
    latitude: defaultLatitude,
    longitude: defaultLongitude,
    padding: defaultPadding,
    pitch: defaultPitch,
    zoom: defaultZoom
  };

  const defaultMapProps: ReactMapGL.MapProps = {
    boxZoom: false,
    cooperativeGestures: true,
    dragRotate: defaultDragRotate,
    logoPosition: 'bottom-right',
    mapboxAccessToken: mapboxApiKey,
    mapStyle: qesLightStyle,
    maxBounds: defaultBoundaries,
    padding: defaultPadding,
    projection: "mercator",
    reuseMaps: false,
    style: { fontFamily: "Avenir Next LT Pro, Calibri, sans-serif" }, // Override ReactMapGL default font-family,
    touchPitch: defaultTouchPitch,
  }

  return (
    <MappingContext.Provider
      value={{
        defaultBearing,
        defaultBoundaries,
        defaultDragRotate,
        defaultLatitude,
        defaultLongitude,
        defaultMapProps,
        defaultPadding,
        defaultPitch,
        defaultTouchPitch,
        defaultViewState,
        defaultZoom,
        errorLoadingSettings,
        loadingDefaultSettings,
        mapboxApiKey,
        qesLightStyle,
        qesOutdoorsStyle,
      }}
    >
      {props.children}
    </MappingContext.Provider>
  );
}

export function withMappingContext<T>(Component: ComponentType<PropsWithChildren<T & MappingContextProps>>): ComponentType<PropsWithChildren<T>> {
  class WithMappingContext extends PureComponent<T> {
    public render() {
      return (
        <MappingContext.Consumer>
          {({
            defaultBearing,
            defaultBoundaries,
            defaultDragRotate,
            defaultLatitude,
            defaultLongitude,
            defaultMapProps,
            defaultPadding,
            defaultPitch,
            defaultTouchPitch,
            defaultViewState,
            defaultZoom,
            errorLoadingSettings,
            loadingDefaultSettings,
            mapboxApiKey,
            qesLightStyle,
            qesOutdoorsStyle
          }) =>
            <Component
              {...this.props}
              defaultBearing={defaultBearing}
              defaultBoundaries={defaultBoundaries}
              defaultDragRotate={defaultDragRotate}
              defaultLatitude={defaultLatitude}
              defaultLongitude={defaultLongitude}
              defaultMapProps={defaultMapProps}
              defaultPadding={defaultPadding}
              defaultPitch={defaultPitch}
              defaultTouchPitch={defaultTouchPitch}
              defaultViewState={defaultViewState}
              defaultZoom={defaultZoom}
              errorLoadingSettings={errorLoadingSettings}
              loadingDefaultSettings={loadingDefaultSettings}
              mapboxApiKey={mapboxApiKey}
              qesLightStyle={qesLightStyle}
              qesOutdoorsStyle={qesOutdoorsStyle}
            />}
        </MappingContext.Consumer>
      );
    }
  }

  return WithMappingContext;
}
